import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import PropTypes from 'prop-types';

// Custom hook for scroll animations
const useInView = (threshold = 0.1) => {
  const [isInView, setIsInView] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsInView(true);
        }
      },
      { threshold }
    );

    if (ref.current) {
      observer.observe(ref.current);
    }

    return () => observer.disconnect();
  }, [threshold]);

  return [ref, isInView];
};

// Section component with scroll animation
const Section = ({ id, title, icon, children, delay = 0 }) => {
  const [ref, isInView] = useInView();

  return (
    <section
      id={id}
      ref={ref}
      className="scroll-mt-24 transition-all duration-700"
      style={{
        opacity: isInView ? 1 : 0,
        transform: isInView ? 'translateY(0)' : 'translateY(40px)',
        transitionDelay: `${delay}s`,
      }}
    >
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg flex items-center justify-center bg-gradient-to-br from-success-20 to-success-10 border border-success-20">
          <span className="text-primary-500">{icon}</span>
        </div>
        <h2 className="text-2xl font-bold text-text-primary">{title}</h2>
      </div>
      {children}
    </section>
  );
};

Section.propTypes = {
  id: PropTypes.string,
  title: PropTypes.string.isRequired,
  icon: PropTypes.node,
  children: PropTypes.node,
  delay: PropTypes.number
};

// Feature card component
const FeatureCard = ({ title, description, items, icon }) => (
  <div className="p-6 rounded-xl h-full bg-gradient-to-br from-bg-secondary/80 to-bg-dark-accent border border-success-10">
    {icon && (
      <div className="w-10 h-10 rounded-lg flex items-center justify-center mb-4 bg-success-10 border border-success-15">
        <span className="text-primary-500">{icon}</span>
      </div>
    )}
    <h3 className="text-lg font-semibold mb-2 text-text-primary">{title}</h3>
    {description && (
      <p className="text-sm mb-4 text-palegreen-70">{description}</p>
    )}
    {items && (
      <ul className="space-y-2">
        {items.map((item, idx) => (
          <li key={idx} className="flex items-start gap-2 text-sm text-palegreen-70">
            <svg className="w-4 h-4 mt-0.5 flex-shrink-0 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
            <span>{item}</span>
          </li>
        ))}
      </ul>
    )}
  </div>
);

FeatureCard.propTypes = {
  title: PropTypes.string.isRequired,
  description: PropTypes.string,
  items: PropTypes.arrayOf(PropTypes.string),
  icon: PropTypes.node
};

// Step component with improved design
const Step = ({ number, title, description, isLast }) => (
  <div className="flex gap-4">
    <div className="flex flex-col items-center">
      <div className="w-10 h-10 rounded-full flex items-center justify-center text-sm font-bold bg-gradient-cta text-black shadow-glow">
        {number}
      </div>
      {!isLast && (
        <div className="w-0.5 flex-1 my-2 bg-gradient-to-b from-success-30 to-success-5" />
      )}
    </div>
    <div className={`flex-1 ${!isLast ? 'pb-8' : ''}`}>
      <h4 className="font-semibold mb-1 text-text-primary">{title}</h4>
      <p className="text-sm leading-relaxed text-palegreen-70">{description}</p>
    </div>
  </div>
);

Step.propTypes = {
  number: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
  title: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  isLast: PropTypes.bool
};

// Code block component with copy button
const CodeBlock = ({ code, language = 'python', title }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(code);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className="rounded-xl overflow-hidden border border-success-15">
      {title && (
        <div className="flex items-center justify-between px-4 py-2 bg-success-8 border-b border-success-10">
          <span className="text-xs font-medium text-primary-500">{title}</span>
          <span className="text-xs px-2 py-0.5 rounded bg-success-15 text-palegreen-70">
            {language}
          </span>
        </div>
      )}
      <div className="relative">
        <pre className="p-4 text-sm font-mono overflow-x-auto bg-bg-primary/50 text-accent-palegreen">
          {code}
        </pre>
        <button
          onClick={handleCopy}
          className={`absolute top-3 right-3 p-2 rounded-lg transition-all duration-200 hover:scale-105 bg-success-10 border border-success-20 ${
            copied ? 'text-primary-500' : 'text-palegreen-60'
          }`}
          title="Copy to clipboard"
        >
          {copied ? (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
      </svg>
          ) : (
            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
      </svg>
          )}
        </button>
      </div>
    </div>
  );
};

CodeBlock.propTypes = {
  code: PropTypes.string.isRequired,
  language: PropTypes.string,
  title: PropTypes.string
};

// Expandable FAQ component
const FAQItem = ({ question, answer, isOpen, onToggle }) => (
  <div className={`rounded-xl overflow-hidden transition-all duration-300 bg-gradient-to-br from-bg-secondary/80 to-bg-dark-accent ${
    isOpen ? 'border border-success-20' : 'border border-success-10'
  }`}>
    <button
      onClick={onToggle}
      className="w-full px-5 py-4 flex items-center justify-between text-left"
    >
      <span className="font-semibold text-text-primary">{question}</span>
      <svg 
        className={`w-5 h-5 transition-transform duration-300 text-primary-500 ${isOpen ? 'rotate-180' : ''}`}
        fill="none" 
        viewBox="0 0 24 24" 
        stroke="currentColor" 
        strokeWidth="2"
      >
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
      </svg>
    </button>
    <div className={`overflow-hidden transition-all duration-300 ${isOpen ? 'max-h-96' : 'max-h-0'}`}>
      <div className="px-5 pb-4">
        <p className="text-sm leading-relaxed text-palegreen-70">{answer}</p>
      </div>
    </div>
  </div>
);

FAQItem.propTypes = {
  question: PropTypes.string.isRequired,
  answer: PropTypes.string.isRequired,
  isOpen: PropTypes.bool,
  onToggle: PropTypes.func
};

// Tip/Note component
const Tip = ({ type = 'tip', children }) => {
  const configs = {
    tip: { 
      bgClass: 'bg-success-8', 
      borderClass: 'border-success-20', 
      textClass: 'text-primary-500',
      label: 'Tip' 
    },
    note: { 
      bgClass: 'bg-info-50', 
      borderClass: 'border-info/20', 
      textClass: 'text-info',
      label: 'Note' 
    },
    warning: { 
      bgClass: 'bg-warning-bg', 
      borderClass: 'border-warning/20', 
      textClass: 'text-warning',
      label: 'Warning' 
    },
  };
  const config = configs[type];

  const icons = {
    tip: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
      </svg>
    ),
    note: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M11.25 11.25l.041-.02a.75.75 0 011.063.852l-.708 2.836a.75.75 0 001.063.853l.041-.021M21 12a9 9 0 11-18 0 9 9 0 0118 0zm-9-3.75h.008v.008H12V8.25z" />
      </svg>
    ),
    warning: (
      <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
        <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z" />
      </svg>
    ),
  };

  return (
    <div className={`p-4 rounded-xl flex gap-3 ${config.bgClass} border ${config.borderClass}`}>
      <span className={`flex-shrink-0 ${config.textClass}`}>{icons[type]}</span>
      <div>
        <span className="text-xs font-semibold uppercase tracking-wider block mb-1 text-palegreen-50">
          {config.label}
        </span>
        <p className="text-sm text-palegreen-80">{children}</p>
      </div>
    </div>
  );
};

Tip.propTypes = {
  type: PropTypes.oneOf(['tip', 'note', 'warning']),
  children: PropTypes.node
};

// Navigation items - ordered by priority
const navItems = [
  { id: 'overview', label: 'Introduction', icon: '⚡' },
  { id: 'quickstart', label: 'Getting Started', icon: '🚀' },
  { id: 'prompts', label: 'Prompt Builder', icon: '✏️' },
  { id: 'variables', label: 'Template Variables', icon: '⚙️' },
  { id: 'testing', label: 'Playground', icon: '▶️' },
  { id: 'sdk', label: 'Python SDK', icon: '💻' },
  { id: 'versions', label: 'Prompt Versioning', icon: '🔄' },
  { id: 'comparison', label: 'Compare Models', icon: '📊' },
  { id: 'workspaces', label: 'Team Workspaces', icon: '📁' },
  { id: 'api-keys', label: 'LLM API Keys', icon: '🔑' },
  { id: 'faq', label: 'Help & FAQ', icon: '❓' },
];

const Documentation = () => {
  const [mounted, setMounted] = useState(false);
  const [activeSection, setActiveSection] = useState('overview');
  const [openFAQ, setOpenFAQ] = useState(0);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  useEffect(() => {
    setMounted(true);

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setActiveSection(entry.target.id);
          }
        });
      },
      { threshold: 0.3, rootMargin: '-100px 0px -50% 0px' }
    );

    navItems.forEach((item) => {
      const element = document.getElementById(item.id);
      if (element) observer.observe(element);
    });

    return () => observer.disconnect();
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      setMobileNavOpen(false);
    }
  };

  const faqs = [
    {
      q: 'Do I need my own API keys?',
      a: 'Yes. You bring your own keys from OpenAI, Anthropic, or Groq. Your keys are encrypted and stored securely. You control your usage and billing directly with the providers. ForgeVault never adds any markup to API costs.',
    },
    {
      q: 'What LLM providers are supported?',
      a: 'We support OpenAI (GPT-4o, GPT-4, O1, O3-mini), Anthropic (Claude Opus, Sonnet, Haiku), and Groq (Llama 3.3, DeepSeek, Qwen, Mixtral). More providers are added regularly.',
    },
    {
      q: 'Can my team collaborate on prompts?',
      a: 'Yes! Invite team members to workspaces with role-based access. Admins have full control, Editors can create and modify prompts, and Viewers can run prompts and view analytics. Everyone shares workspace API keys without seeing the actual values.',
    },
    {
      q: 'Is my data secure?',
      a: 'All data is encrypted in transit (TLS 1.3) and at rest (AES-256). API keys are encrypted with industry-standard algorithms and never displayed in full. We do not train on your prompts or share your data with third parties.',
    },
    {
      q: 'How does version control work?',
      a: 'Every time you save a prompt, a new version is created automatically with a timestamp. You can view the complete history, see visual diffs between any two versions, and rollback to any previous version with one click.',
    },
    {
      q: 'Can I export my prompts?',
      a: 'Yes! Export prompts as Python code (compatible with OpenAI, Anthropic, or LangChain), cURL commands for testing, or Markdown for documentation. Your prompts belong to you — export everything anytime.',
    },
    {
      q: 'Is there a free tier?',
      a: 'Yes, ForgeVault is free to start. The free tier includes unlimited prompts and workspaces. You only pay for the LLM API usage directly to the providers (OpenAI, Anthropic, etc.).',
    },
  ];

  return (
    <div className="min-h-screen bg-transparent">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-12 py-8 lg:py-12">
        
        {/* Hero */}
        <div 
          className="text-center mb-12 lg:mb-16 transition-all duration-700"
          style={{
            opacity: mounted ? 1 : 0,
            transform: mounted ? 'translateY(0)' : 'translateY(30px)',
          }}
        >
          {/* Logo */}
          <div className="flex justify-center mb-8">
            <img 
              src="/logo.png"
              alt="ForgeVault Logo" 
              className="w-20 h-20 lg:w-24 lg:h-24 object-contain"
            />
          </div>
          
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold mb-4 text-text-primary">
            Learn ForgeVault
          </h1>
          <p className="text-base sm:text-lg lg:text-xl max-w-2xl mx-auto mb-8 text-palegreen-70">
            Everything you need to build, test, and deploy AI prompts
          </p>

          {/* Quick Links */}
          <div className="flex flex-wrap justify-center gap-3">
            <button
              onClick={() => scrollToSection('quickstart')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 bg-gradient-cta text-black"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
              </svg>
              Quick Start
            </button>
            <button
              onClick={() => scrollToSection('sdk')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 bg-success-10 border border-success-20 text-primary-500"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
              </svg>
              SDK Guide
            </button>
            <button
              onClick={() => scrollToSection('prompts')}
              className="inline-flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 hover:-translate-y-0.5 bg-success-10 border border-success-20 text-primary-500"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
              </svg>
              Creating Prompts
            </button>
        </div>
      </div>

        {/* Mobile Navigation Toggle */}
        <div className="lg:hidden mb-6">
              <button
            onClick={() => setMobileNavOpen(!mobileNavOpen)}
            className="w-full flex items-center justify-between px-4 py-3 rounded-lg bg-bg-secondary border border-success-10 text-text-primary"
          >
            <span className="flex items-center gap-2">
              <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
              </svg>
              <span className="font-medium">Jump to section</span>
                </span>
            <svg className={`w-5 h-5 transition-transform ${mobileNavOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
            </svg>
          </button>
          
          {mobileNavOpen && (
            <div className="mt-2 p-2 rounded-lg bg-bg-secondary border border-success-10">
              {navItems.map((item) => (
                <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 ${
                    activeSection === item.id 
                      ? 'bg-success-10 text-primary-500' 
                      : 'text-palegreen-60 hover:bg-success-5'
                  }`}
                >
                  <span className="mr-2">{item.icon}</span>
                  {item.label}
              </button>
            ))}
            </div>
          )}
        </div>

        {/* Main Layout */}
        <div className="flex gap-8 lg:gap-12">
          
          {/* Sidebar Navigation - Desktop */}
          <nav 
            className="hidden lg:block w-56 flex-shrink-0 transition-all duration-700"
            style={{
              opacity: mounted ? 1 : 0,
              transform: mounted ? 'translateX(0)' : 'translateX(-20px)',
            }}
          >
            <div className="sticky top-24 space-y-1">
              <div className="text-xs font-semibold uppercase tracking-wider mb-3 px-4 text-palegreen-50">
                Contents
              </div>
              {navItems.map((item) => (
                  <button
                  key={item.id}
                  onClick={() => scrollToSection(item.id)}
                  className={`block w-full text-left px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-200 border-l-2 ${
                    activeSection === item.id 
                      ? 'bg-success-10 text-primary-500 border-primary-500' 
                      : 'text-palegreen-60 border-transparent hover:bg-success-5 hover:text-palegreen-80'
                  }`}
                >
                  {item.label}
                  </button>
                ))}

              {/* SDK Install */}
              <div className="mt-6 pt-6 border-t border-success-10">
                <div className="text-xs font-semibold uppercase tracking-wider mb-3 px-4 text-palegreen-50">
                  Quick Install
                </div>
                <div className="mx-4 p-3 rounded-lg text-xs font-mono bg-bg-primary/30 text-accent-palegreen">
                  pip install forgevault
                </div>
              </div>
            </div>
              </nav>

          {/* Content */}
          <div className="flex-1 min-w-0 space-y-16 lg:space-y-20">

            {/* Overview */}
            <Section 
              id="overview" 
              title="What is ForgeVault?"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M13 10V3L4 14h7v7l9-11h-7z" />
                </svg>
              }
            >
              <p className="text-base lg:text-lg mb-8 leading-relaxed text-palegreen-80">
                ForgeVault is a prompt management platform that helps teams build AI features together. 
                Product owners can create prompts using natural language, while developers integrate them via a simple SDK.
              </p>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
                <FeatureCard 
                  title="For Product Teams"
                  description="Create AI features in plain English. No coding required — describe what you need and get a production-ready prompt."
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M12 18v-5.25m0 0a6.01 6.01 0 001.5-.189m-1.5.189a6.01 6.01 0 01-1.5-.189m3.75 7.478a12.06 12.06 0 01-4.5 0m3.75 2.383a14.406 14.406 0 01-3 0M14.25 18v-.192c0-.983.658-1.823 1.508-2.316a7.5 7.5 0 10-7.517 0c.85.493 1.509 1.333 1.509 2.316V18" />
                    </svg>
                  }
                />
                <FeatureCard 
                  title="For Developers"
                  description="Clean SDK integration with version pinning and caching. Fetch prompts at runtime — no prompt strings in your codebase."
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                    </svg>
                  }
                />
                <FeatureCard 
                  title="For Teams"
                  description="Shared workspaces with role-based access. Everyone collaborates on the same prompts with version history."
                  icon={
                    <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M18 18.72a9.094 9.094 0 003.741-.479 3 3 0 00-4.682-2.72m.94 3.198l.001.031c0 .225-.012.447-.037.666A11.944 11.944 0 0112 21c-2.17 0-4.207-.576-5.963-1.584A6.062 6.062 0 016 18.719m12 0a5.971 5.971 0 00-.941-3.197m0 0A5.995 5.995 0 0012 12.75a5.995 5.995 0 00-5.058 2.772m0 0a3 3 0 00-4.681 2.72 8.986 8.986 0 003.74.477m.94-3.197a5.971 5.971 0 00-.94 3.197M15 6.75a3 3 0 11-6 0 3 3 0 016 0zm6 3a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0zm-13.5 0a2.25 2.25 0 11-4.5 0 2.25 2.25 0 014.5 0z" />
                    </svg>
                  }
                />
            </div>

              <Tip type="tip">
                New to prompt engineering? Start with AI-Assisted mode — just describe what you want and we'll generate the prompt for you.
              </Tip>
            </Section>

            {/* Quick Start */}
            <Section 
              id="quickstart" 
              title="Quick Start"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 3v4M3 5h4M6 17v4m-2-2h4m5-16l2.286 6.857L21 12l-5.714 2.143L13 21l-2.286-6.857L5 12l5.714-2.143L13 3z" />
                </svg>
              }
              delay={0.1}
            >
              <p className="text-base mb-8 text-palegreen-70">
                Get your first prompt running in under 5 minutes.
              </p>
              
              <div className="space-y-0">
                <Step number={1} title="Create an Account" description="Sign up with your email or Google account. No credit card required to start." />
                <Step number={2} title="Set Up a Workspace" description="Workspaces organize your prompts by project or team. Create one for your first project." />
                <Step number={3} title="Add API Keys" description="Go to Settings → API Keys and add your OpenAI, Anthropic, or Groq API key. Keys are encrypted and stored securely." />
                <Step number={4} title="Create Your First Prompt" description="Click 'New Prompt' and choose AI-Assisted mode. Describe what you want the AI to do in plain English." />
                <Step number={5} title="Test & Iterate" description="Run your prompt with test inputs. Review the output, refine if needed, then export to your codebase." isLast />
                  </div>

              <div className="mt-8">
                <Tip type="note">
                  You can also create prompts manually if you prefer full control over the system and user prompts.
                </Tip>
                          </div>
            </Section>

            {/* Creating Prompts */}
            <Section 
              id="prompts" 
              title="Creating Prompts"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                                    </svg>
              }
              delay={0.1}
            >
              <p className="text-base mb-8 text-palegreen-70">
                ForgeVault offers two ways to create prompts — choose based on your preference and expertise level.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="p-6 rounded-xl bg-gradient-to-br from-success-10 to-success-5 border border-success-20">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-primary-500" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.456 2.456L21.75 6l-1.035.259a3.375 3.375 0 00-2.456 2.456z" />
                    </svg>
                    <h3 className="text-lg font-semibold text-primary-500">AI-Assisted Mode</h3>
                          </div>
                  <p className="text-sm mb-4 text-palegreen-70">
                    Describe what you want in plain English. Our AI generates a complete prompt with system instructions, 
                    user templates, and variables. Review and refine the output before saving.
                  </p>
                  <div className="text-xs font-medium text-palegreen-50">
                    Best for: New prompts, exploration, non-technical users
                        </div>
                    </div>

                <div className="p-6 rounded-xl bg-gradient-to-br from-bg-secondary/80 to-bg-dark-accent border border-success-10">
                  <div className="flex items-center gap-2 mb-3">
                    <svg className="w-5 h-5 text-accent-palegreen" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                    </svg>
                    <h3 className="text-lg font-semibold text-text-primary">Manual Mode</h3>
                          </div>
                  <p className="text-sm mb-4 text-palegreen-70">
                    Write your own system and user prompts with full control. Define variables, set model parameters, 
                    and fine-tune every aspect of your prompt.
                  </p>
                  <div className="text-xs font-medium text-palegreen-50">
                    Best for: Migrating existing prompts, precise control, advanced users
                          </div>
                        </div>
                    </div>

              <h3 className="text-lg font-semibold mb-4 text-text-primary">Prompt Structure</h3>
              <p className="text-sm mb-4 text-palegreen-70">
                Every prompt in ForgeVault has three main components:
              </p>
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <FeatureCard 
                  title="System Prompt"
                  description="Instructions that define the AI's behavior, role, and constraints. This sets the context for all interactions."
                />
                <FeatureCard 
                  title="User Prompt"
                  description="The template for user input. Contains dynamic variables that get filled in at runtime."
                />
                <FeatureCard 
                  title="Model Settings"
                  description="Temperature, max tokens, and other parameters that control how the model generates responses."
                />
              </div>
            </Section>

            {/* Variables */}
            <Section 
              id="variables" 
              title="Working with Variables"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M10.343 3.94c.09-.542.56-.94 1.11-.94h1.093c.55 0 1.02.398 1.11.94l.149.894c.07.424.384.764.78.93.398.164.855.142 1.205-.108l.737-.527a1.125 1.125 0 011.45.12l.773.774c.39.389.44 1.002.12 1.45l-.527.737c-.25.35-.272.806-.107 1.204.165.397.505.71.93.78l.893.15c.543.09.94.56.94 1.109v1.094c0 .55-.397 1.02-.94 1.11l-.893.149c-.425.07-.765.383-.93.78-.165.398-.143.854.107 1.204l.527.738c.32.447.269 1.06-.12 1.45l-.774.773a1.125 1.125 0 01-1.449.12l-.738-.527c-.35-.25-.806-.272-1.203-.107-.397.165-.71.505-.781.929l-.149.894c-.09.542-.56.94-1.11.94h-1.094c-.55 0-1.019-.398-1.11-.94l-.148-.894c-.071-.424-.384-.764-.781-.93-.398-.164-.854-.142-1.204.108l-.738.527c-.447.32-1.06.269-1.45-.12l-.773-.774a1.125 1.125 0 01-.12-1.45l.527-.737c.25-.35.273-.806.108-1.204-.165-.397-.505-.71-.93-.78l-.894-.15c-.542-.09-.94-.56-.94-1.109v-1.094c0-.55.398-1.02.94-1.11l.894-.149c.424-.07.765-.383.93-.78.165-.398.143-.854-.107-1.204l-.527-.738a1.125 1.125 0 01.12-1.45l.773-.773a1.125 1.125 0 011.45-.12l.737.527c.35.25.807.272 1.204.107.397-.165.71-.505.78-.929l.15-.894z" />
                              </svg>
              }
              delay={0.1}
            >
              <p className="text-base mb-6 text-palegreen-70">
                Variables make your prompts dynamic. Define placeholders that get filled in at runtime.
              </p>
              
              <div className="mb-6">
                <CodeBlock 
                  title="Example: Customer Support Prompt"
                  language="text"
                  code={`You are a {{role}} assistant for {{company_name}}.

User's question: {{user_question}}

Guidelines:
- Respond in {{language}}
- Use a {{tone}} tone
- Keep responses under {{max_words}} words`} 
                />
                          </div>

              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <FeatureCard 
                  title="Variable Syntax"
                  items={[
                    'Use double curly braces: {{variable_name}}',
                    'Use snake_case for multi-word names',
                    'Variables are case-sensitive',
                    'Same variable can be used multiple times',
                  ]}
                />
                <FeatureCard 
                  title="At Runtime"
                  items={[
                    'Fill values through UI when testing',
                    'Pass as dictionary via SDK',
                    'Missing variables return an error',
                    'Empty strings are allowed',
                  ]}
                />
                          </div>

              <Tip type="warning">
                Variable names must be alphanumeric with underscores. Avoid spaces or special characters.
              </Tip>
            </Section>

            {/* Testing */}
            <Section 
              id="testing" 
              title="Running & Testing"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <polygon points="5 3 19 12 5 21 5 3" />
                </svg>
              }
              delay={0.1}
            >
              <p className="text-base mb-8 text-palegreen-70">
                Test prompts against real LLM APIs and get detailed metrics for every run.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <FeatureCard 
                  title="Real-Time Execution"
                  items={[
                    'Run against OpenAI, Anthropic, or Groq',
                    'Fill variables through a clean interface',
                    'Stream responses in real-time',
                    'Save every run for comparison',
                  ]}
                />
                <FeatureCard 
                  title="Comprehensive Metrics"
                  items={[
                    'Token usage (input + output)',
                    'Cost estimation per run',
                    'Latency and time-to-first-token',
                    'Success rate tracking',
                  ]}
                />
                        </div>

              <Tip type="tip">
                Run History saves all your test runs. Use it to compare outputs across different variable values or model settings.
              </Tip>
            </Section>

            {/* SDK */}
            <Section 
              id="sdk" 
              title="SDK Integration"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M17.25 6.75L22.5 12l-5.25 5.25m-10.5 0L1.5 12l5.25-5.25m7.5-3l-4.5 16.5" />
                </svg>
              }
              delay={0.1}
            >
              <p className="text-base mb-6 text-palegreen-70">
                Use your prompts in production with our Python SDK. Install from PyPI and integrate in minutes.
              </p>
              
              <div className="space-y-4 mb-8">
                <CodeBlock 
                  title="Installation"
                  language="bash"
                  code={`pip install forgevault`} 
                />
                
                <CodeBlock 
                  title="Basic Usage"
                  language="python"
                  code={`from forgevault import ForgeVault

# Initialize the client
client = ForgeVault(api_key="your-forgevault-api-key")

# Get a prompt by name
prompt = client.get_prompt("customer-support-bot")

# Use with your LLM
response = prompt.run(
    variables={
        "user_question": "How do I reset my password?",
        "language": "English",
        "tone": "friendly"
    },
    model="gpt-4o"
)

print(response.content)`} 
                />

                <CodeBlock 
                  title="With Version Pinning"
                  language="python"
                  code={`# Pin to a specific version for production stability
prompt = client.get_prompt(
    "customer-support-bot",
    version="v3"  # Always use this version
)

# Or use latest (default)
prompt = client.get_prompt("customer-support-bot")  # Uses latest`} 
                />
                    </div>
              
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                <FeatureCard 
                  title="SDK Features"
                  items={[
                    'Version pinning for production stability',
                    'Built-in caching with configurable TTL',
                    'Offline fallback (uses cached prompts)',
                    'Full async/await support',
                    'Automatic retry with exponential backoff',
                  ]}
                />
                <FeatureCard 
                  title="Export Formats"
                  items={[
                    'Python (OpenAI SDK compatible)',
                    'Python (Anthropic SDK compatible)',
                    'Python (LangChain compatible)',
                    'cURL for quick testing',
                    'Markdown for documentation',
                  ]}
                />
              </div>

              <Tip type="note">
                The SDK fetches prompts at runtime. Updates made in ForgeVault are available immediately without redeploying your application (unless you're using version pinning).
              </Tip>
            </Section>

            {/* Version Control */}
            <Section 
              id="versions" 
              title="Version Control"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M7.5 21L3 16.5m0 0L7.5 12M3 16.5h13.5m0-13.5L21 7.5m0 0L16.5 12M21 7.5H7.5" />
                      </svg>
              }
              delay={0.1}
            >
              <p className="text-base mb-8 text-palegreen-70">
                Every change is tracked automatically. Compare versions, see diffs, and rollback instantly.
              </p>
              
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <FeatureCard 
                  title="Auto-Save"
                  description="Every edit creates a new version with timestamp and description. Nothing is ever lost."
                />
                <FeatureCard 
                  title="Visual Diff"
                  description="See exactly what changed between any two versions with highlighted additions and deletions."
                />
                <FeatureCard 
                  title="Instant Rollback"
                  description="Restore any previous version with one click. The rollback itself creates a new version."
                />
                      </div>
            </Section>

            {/* Model Comparison */}
            <Section 
              id="comparison" 
              title="Model Comparison"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <rect x="3" y="3" width="7" height="18" rx="1" />
                  <rect x="14" y="3" width="7" height="18" rx="1" />
                </svg>
              }
              delay={0.1}
            >
              <p className="text-base mb-8 text-palegreen-70">
                Run the same prompt across multiple models and compare quality, cost, and speed side-by-side.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <FeatureCard 
                  title="Supported Providers"
                  items={[
                    'OpenAI: GPT-4o, GPT-4, O1, O3-mini',
                    'Anthropic: Claude Opus, Sonnet, Haiku',
                    'Groq: Llama 3.3, DeepSeek, Qwen, Mixtral',
                  ]}
                />
                <FeatureCard 
                  title="Comparison Metrics"
                  items={[
                    'Output quality (side-by-side view)',
                    'Token usage and cost per model',
                    'Response latency',
                    'Win rate across multiple runs',
                  ]}
                />
                    </div>

              <Tip type="tip">
                Use Model Comparison to find the most cost-effective model for your use case. Sometimes a cheaper model delivers similar quality.
              </Tip>
            </Section>

            {/* Workspaces */}
            <Section 
              id="workspaces" 
              title="Workspaces"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10" />
                      </svg>
              }
              delay={0.1}
            >
              <p className="text-base mb-8 text-palegreen-70">
                Workspaces keep your prompts, API keys, and team members organized and isolated.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <FeatureCard 
                  title="Workspace Isolation"
                  items={[
                    'Separate prompts by project or client',
                    'Independent API key management per workspace',
                    'Per-workspace usage tracking and analytics',
                    'Switch between workspaces instantly',
                  ]}
                />
                <FeatureCard 
                  title="Team Collaboration"
                  items={[
                    'Invite members by email',
                    'Admin, Editor, and Viewer roles',
                    'Shared API keys (actual values hidden)',
                    'Activity tracking per team member',
                  ]}
                />
                      </div>

              <Tip type="tip">
                Create separate workspaces for different clients or projects to keep API keys and billing separate.
              </Tip>
            </Section>

            {/* API Keys */}
            <Section 
              id="api-keys" 
              title="Managing API Keys"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 5.25a3 3 0 013 3m3 0a6 6 0 01-7.029 5.912c-.563-.097-1.159.026-1.563.43L10.5 17.25H8.25v2.25H6v2.25H2.25v-2.818c0-.597.237-1.17.659-1.591l6.499-6.499c.404-.404.527-1 .43-1.563A6 6 0 1121.75 8.25z" />
                </svg>
              }
              delay={0.1}
            >
              <p className="text-base mb-8 text-palegreen-70">
                API keys are stored securely and used to run prompts against LLM providers. You bring your own keys.
              </p>
              
              <div className="grid md:grid-cols-2 gap-4 mb-8">
                <FeatureCard 
                  title="Security"
                  items={[
                    'Keys encrypted at rest with AES-256',
                    'Never displayed in full after saving',
                    'Transmitted only over TLS 1.3',
                    'Per-workspace isolation',
                  ]}
                />
                <FeatureCard 
                  title="Supported Providers"
                  items={[
                    'OpenAI (required for GPT models)',
                    'Anthropic (required for Claude models)',
                    'Groq (required for Llama, DeepSeek, etc.)',
                  ]}
                />
                    </div>

              <Tip type="warning">
                ForgeVault never stores or logs your API responses. We only track metadata like token counts and latency for analytics.
              </Tip>
            </Section>

            {/* FAQ */}
            <Section 
              id="faq" 
              title="Frequently Asked Questions"
              icon={
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
              }
              delay={0.1}
            >
              <div className="space-y-3">
                {faqs.map((faq, idx) => (
                  <FAQItem
                    key={idx}
                    question={faq.q}
                    answer={faq.a}
                    isOpen={openFAQ === idx}
                    onToggle={() => setOpenFAQ(openFAQ === idx ? -1 : idx)}
                  />
                ))}
                </div>
            </Section>

            {/* CTA */}
            <div 
              className="text-center pt-8 transition-opacity duration-700"
              style={{
                opacity: mounted ? 1 : 0,
                transitionDelay: '0.5s',
              }}
            >
              <div className="p-6 sm:p-8 lg:p-12 rounded-2xl bg-gradient-to-br from-success-10 to-success-5 border border-success-20">
                <h2 className="text-xl sm:text-2xl lg:text-3xl font-bold mb-4 text-text-primary">
                  Ready to get started?
                </h2>
                <p className="text-sm sm:text-base mb-8 max-w-lg mx-auto text-palegreen-60">
                  Create your free account and start building AI features in minutes.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <Link
                    to="/signup"
                    className="inline-flex items-center justify-center gap-2 px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-semibold rounded-xl transition-all duration-300 hover:-translate-y-0.5 bg-gradient-cta text-black shadow-glow"
                  >
                    Get Started Free
                    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M13 7l5 5m0 0l-5 5m5-5H6" />
                    </svg>
                  </Link>
                  <Link
                    to="/features"
                    className="inline-flex items-center justify-center px-6 sm:px-8 py-3 sm:py-4 text-sm sm:text-base font-medium rounded-xl transition-all duration-300 hover:bg-white/5 border border-success-30 text-palegreen-80"
                  >
                    Explore Features
                  </Link>
            </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default Documentation;
